import { useEffect, useRef, useState, FormEvent } from 'react'
import { tenantConfig } from '../config/tenant'
import { api } from '../api/client'

/**
 * Exit-intent lead capture. When a desktop visitor moves the cursor out the top
 * of the window (about to close the tab or hit the address bar), we offer a free
 * rate check and capture their details as a normal lead (source=exit_intent), so
 * it flows through the same broker-notification + billing pipeline as any lead.
 *
 * Shown at most once per browser session (sessionStorage), dismissible, and
 * suppressed on the /dataentry portal. No em-dashes in any visible copy.
 */
const SESSION_KEY = 'gs_exit_intent_shown'

function alreadyShown(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1'
  } catch {
    return false
  }
}

function markShown(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1')
  } catch {
    /* ignore */
  }
}

export default function ExitIntentModal({ armDelayMs = 3000 }: { armDelayMs?: number } = {}) {
  const [open, setOpen] = useState(false)
  const mountedAt = useRef(Date.now())
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof document === 'undefined') return
    // Skip on the broker portal and if we've already offered this session.
    if (window.location.pathname.startsWith('/dataentry')) return
    if (alreadyShown()) return

    function fire() {
      // Arm delay so it never fires on initial load; once per session.
      if (Date.now() - mountedAt.current < armDelayMs) return
      if (alreadyShown()) return
      markShown()
      setOpen(true)
      cleanup()
    }
    // 1) Cursor leaves the viewport through the top (toward tabs / close / address bar).
    function onMouseOut(e: MouseEvent) {
      if (e.clientY <= 0 && e.relatedTarget == null) fire()
    }
    // 2) Cursor leaves the whole page area through the top (more reliable than
    //    document mouseout). We deliberately do NOT trigger on tab switch /
    //    visibilitychange: returning to the tab is not "leaving", and firing then
    //    is jarring. Exit-intent is desktop mouse-out only.
    function onMouseLeave(e: MouseEvent) {
      if (e.clientY <= 0) fire()
    }
    function cleanup() {
      document.removeEventListener('mouseout', onMouseOut)
      document.documentElement.removeEventListener('mouseleave', onMouseLeave)
    }
    document.addEventListener('mouseout', onMouseOut)
    document.documentElement.addEventListener('mouseleave', onMouseLeave)
    return cleanup
  }, [armDelayMs])

  function close() {
    setOpen(false)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await api.post('/api/leads', {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        loan_purpose: 'Free rate check',
        source: 'exit_intent',
        loan_details: { trigger: 'exit_intent' },
      })
      setDone(true)
    } catch {
      setError('Something went wrong. Please try again or call us.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  const inputCls =
    'w-full px-4 py-3 rounded-xl bg-white border border-[#D8D8D0] text-ink placeholder-gs-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-[#E2FF60]'

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
    >
      <div
        className="relative w-full max-w-md rounded-3xl p-8 shadow-2xl bg-white border border-[#E8E8E2]"
        role="dialog"
        aria-modal="true"
        aria-label="Free rate check"
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-full text-gs-text-muted hover:text-ink hover:bg-[#F4F4F0]"
        >
          <span aria-hidden="true" className="text-lg">×</span>
        </button>

        {done ? (
          <div className="text-center py-4">
            <div
              className="w-12 h-12 rounded-full grid place-items-center mx-auto mb-4"
              style={{ background: tenantConfig.accentColor }}
            >
              <span className="text-ink text-xl" aria-hidden="true">✓</span>
            </div>
            <h2 className="font-heading text-2xl font-semibold text-ink mb-2">You&apos;re all set.</h2>
            <p className="text-gs-text-soft text-sm">
              One of our brokers will send your personalised rate comparison shortly. Talk soon.
            </p>
            <button
              onClick={close}
              className="mt-6 px-6 py-3 rounded-xl text-sm font-semibold"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <img
              src={tenantConfig.logoImageUrl}
              alt={`${tenantConfig.name} logo`}
              className="h-14 w-14 object-contain rounded-2xl mb-5"
            />
            <h2 className="font-heading text-2xl font-semibold text-ink leading-tight mb-2">
              Before you go, get a free rate check.
            </h2>
            <p className="text-gs-text-soft text-sm mb-6">
              We compare {tenantConfig.lenderCount}+ lenders in about 15 minutes. No obligation, no
              pressure. Leave your details and one of our brokers will send you a personalised rate
              comparison.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <input aria-label="Your name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} />
              <input aria-label="Email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className={inputCls} />
              <input aria-label="Phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone" className={inputCls} />
              {error && <div className="text-red-600 text-sm">{error}</div>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-semibold disabled:opacity-60"
                style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
              >
                {submitting ? 'Sending…' : 'Get my free rate check'}
              </button>
              <p className="text-center text-xs text-gs-text-muted">No spam. Unsubscribe anytime.</p>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
